/* eslint-disable no-console */
import axios from 'axios';

import {
  CLIMATE_DB_BASE_URL,
  CLIMATE_DB_ENDPOINT_JSON
} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import {ensureAIToken} from '../ClimateDB/aiTokenAPI';

const ASK_AI_COLLECTIONS: Record<string, string> = {
  '/brsreport/generaldisclosure': 'generaldisclosures',
  '/brsreport/mapdisclosure': 'mapdisclosures',
  '/brsreport/pwpdisclosure': 'pwpdisclosures'
};

export interface AskAIAnswer {
  answer_text?: string | null;
  yes_no?: string | null;
}

interface AskAIAnswerResponse {
  answer?: AskAIAnswer | null;
}

export interface AskAIPromptInput {
  companyId: string;
  year: string;
  questionKey: string;
  currentAnswer: string | null;
  platformKeys: string[];
  regenerate?: boolean;
}

/** Climate DB collection that stores a BRSR questionnaire route. */
export const askAICollection = (apiRoute: string): string => {
  const known = ASK_AI_COLLECTIONS[apiRoute];

  if (known) {
    return known;
  }

  const segment = apiRoute.split('/').filter(Boolean).pop() ?? '';

  if (!segment) {
    return '';
  }

  return segment.endsWith('s') ? segment : `${segment}s`;
};

const answerText = (answer: AskAIAnswer | null | undefined): string => {
  const narrative = answer?.answer_text?.trim() ?? '';

  if (narrative) {
    return narrative;
  }

  return answer?.yes_no?.trim() ?? '';
};

/**
 * Drafts an ESG disclosure answer.
 * `POST /api/ask-ai/answer` on the Climate DB, authorised with the Climate DB token.
 */
export const askAIPrompt = async (
  input: AskAIPromptInput
): Promise<string | undefined> => {
  const companyId = input.companyId.trim();
  const year = input.year.trim();
  const questionKey = input.questionKey.trim();
  const currentAnswer = input.currentAnswer?.trim() ?? '';
  const token = await ensureAIToken();

  if (!token || !companyId || !year || !questionKey) {
    return undefined;
  }

  try {
    const response = await axios.request<AskAIAnswerResponse>({
      method: CRUDMethods.POST,
      url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.askAiAnswer}`,
      headers: {Authorization: `Bearer ${token}`},
      timeout: 120000,
      data: {
        mongo_company_id: companyId,
        year,
        question_key: questionKey,
        current_answer: currentAnswer === '' ? null : currentAnswer,
        platform_keys: input.platformKeys,
        regenerate: input.regenerate ?? false
      }
    });
    const text = answerText(response.data?.answer);

    if (!text) {
      return undefined;
    }

    return text;
  } catch (err) {
    console.log('ask ai err:', err);

    return undefined;
  }
};
